/** A fita do registrador: o saldo no fim de cada dia sobre as faixas de saúde impressas
 * no papel, com a agulha em hoje e o menor saldo marcado.
 *
 * É desenhada 1:1 com a largura medida (viewBox = px reais), para o texto gravado não
 * encolher no celular. Sem biblioteca de gráfico: o desenho é do mundo, não genérico.
 */

import { useEffect, useRef, useState } from "react";
import { dayMonth, short } from "../lib/format";
import { zoneClass } from "./ui";
import type { Day, HealthLevel, ZoneColor } from "../lib/types";

const ROTULOS: Record<string, string> = {
  crit: "Crítico",
  urg: "Urgente",
  att: "Atenção",
  ok: "Saudável",
  abn: "Abundante",
};

/** As faixas pintadas atrás da fita são as do usuário, como na régua. Estavam cravadas
 *  aqui: depois de editar "Saudável" para começar em R$ 8.000, o cartão dizia ATENÇÃO e a
 *  fita continuava desenhando o mesmo saldo dentro da banda verde. */
const BANDS_PADRAO: { id: string; label: string; from: number; to: number }[] = [
  { id: "crit", label: "Crítico", from: -Infinity, to: 0 },
  { id: "urg", label: "Urgente", from: 0, to: 1000 },
  { id: "att", label: "Atenção", from: 1000, to: 3000 },
  { id: "ok", label: "Saudável", from: 3000, to: 10000 },
  { id: "abn", label: "Abundante", from: 10000, to: Infinity },
];

function bandasDe(levels?: HealthLevel[]) {
  if (!levels?.length) return BANDS_PADRAO;
  return [...levels]
    .sort((a, b) => (a.min ?? -Infinity) - (b.min ?? -Infinity))
    .map((l) => {
      const id = zoneClass(l.color as ZoneColor);
      return { id, label: l.label || ROTULOS[id] || "", from: l.min ?? -Infinity, to: l.max ?? Infinity };
    });
}

export function useMeasuredWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    ro.observe(el);
    setWidth(Math.round(el.clientWidth));
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

export function Tape({
  days,
  today,
  levels,
}: {
  days: Day[];
  today: string;
  levels?: HealthLevel[];
}) {
  const { ref, width } = useMeasuredWidth();
  return (
    <div className="tapebox" ref={ref}>
      {width > 0 && days.length > 1 && (
        <TapeSvg days={days} today={today} levels={levels} W={Math.max(300, width)} />
      )}
    </div>
  );
}

function TapeSvg({
  days,
  today,
  levels,
  W,
}: {
  days: Day[];
  today: string;
  levels?: HealthLevel[];
  W: number;
}) {
  const BANDS = bandasDe(levels);
  const H = W < 480 ? 176 : 210;
  const L = 8;
  const R = W < 480 ? 82 : 96;
  const T = 16;
  const B = 26;

  const values = days.map((d) => d.end);
  let lo = Math.min(0, ...values);
  let hi = Math.max(0, ...values);
  const pad = (hi - lo) * 0.12 || 500;
  lo -= pad;
  hi += pad;

  const x = (i: number) => L + (i / (days.length - 1)) * (W - L - R);
  const y = (v: number) => T + (1 - (v - lo) / (hi - lo)) * (H - T - B);

  const todayIndex = days.findIndex((d) => d.date === today);
  const worstIndex = days.reduce((m, d, i) => (d.end < days[m].end ? i : m), 0);
  const worst = days[worstIndex];
  const points = days.map((d, i) => `${x(i).toFixed(1)},${y(d.end).toFixed(1)}`).join(" ");
  const floor = y(Math.max(lo, 0));

  // rótulos do eixo: pula o que colide com a agulha e o que fica perto do anterior
  const ticks: number[] = [];
  const step = W < 480 ? 6 : 5;
  let lastX = -Infinity;
  days.forEach((_, i) => {
    const wanted = i % step === 0 || i === days.length - 1;
    if (!wanted) return;
    if (todayIndex >= 0 && Math.abs(x(i) - x(todayIndex)) < 24) return;
    if (x(i) - lastX < 44) return;
    lastX = x(i);
    ticks.push(i);
  });

  const worstOnRight = x(worstIndex) > (W - R) * 0.6;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      role="img"
      aria-label={`Saldo projetado por dia. Menor saldo ${short(worst.end)} em ${dayMonth(worst.date)}.`}
    >
      <defs>
        {/* A área sob o traço ganha volume: densa junto da curva, dissolvendo até a base.
            Sem isso a fita é um fio preto sobre faixas pastel — o que o dono chamou de
            "sem vida". */}
        <linearGradient id="tape-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--ink)" stopOpacity=".26" />
          <stop offset=".55" stopColor="var(--ink)" stopOpacity=".10" />
          <stop offset="1" stopColor="var(--ink)" stopOpacity=".02" />
        </linearGradient>
      </defs>

      {BANDS.map((b) => {
        const y1 = y(Math.min(hi, b.to === Infinity ? hi : b.to));
        const y2 = y(Math.max(lo, b.from === -Infinity ? lo : b.from));
        if (y2 - y1 <= 0.5) return null;
        return (
          <g key={b.id}>
            <rect
              className="z-band"
              x={L}
              y={y1}
              width={W - L - R}
              height={y2 - y1}
              fill={`var(--z-${b.id}-bg)`}
            />
            {y2 - y1 >= 14 && (
              <text className="zl" x={W - R + 6} y={(y1 + y2) / 2 + 3}>
                {b.label}
              </text>
            )}
          </g>
        );
      })}

      {days.map((_, i) =>
        i % 5 === 0 ? (
          <line key={`g${i}`} className="gridline" x1={x(i)} x2={x(i)} y1={T} y2={H - B} />
        ) : null,
      )}

      {lo < 0 && hi > 0 && <line className="zero" x1={L} x2={W - R} y1={y(0)} y2={y(0)} />}

      <polygon className="fill" points={`${x(0)},${floor} ${points} ${x(days.length - 1)},${floor}`} />
      <polyline className="trace" points={points} />

      {todayIndex >= 0 && (
        <>
          <line className="needle" x1={x(todayIndex)} x2={x(todayIndex)} y1={T - 6} y2={H - B} />
          <polygon
            className="needle-head"
            points={`${x(todayIndex) - 4},${T - 8} ${x(todayIndex) + 4},${T - 8} ${x(todayIndex)},${T - 2}`}
          />
          <text
            x={x(todayIndex)}
            y={H - 8}
            textAnchor={todayIndex === 0 ? "start" : "middle"}
          >
            hoje
          </text>
        </>
      )}

      <circle className="dot min" cx={x(worstIndex)} cy={y(worst.end)} r={3.5} />
      <text
        className="val"
        x={x(worstIndex) + (worstOnRight ? -8 : 8)}
        y={Math.max(T + 12, y(worst.end) - 9)}
        textAnchor={worstOnRight ? "end" : "start"}
      >
        {short(worst.end)} · {dayMonth(worst.date)}
      </text>

      {ticks.map((i) => (
        <text
          key={`t${i}`}
          x={x(i)}
          y={H - 8}
          textAnchor={i === 0 ? "start" : i === days.length - 1 ? "end" : "middle"}
        >
          {dayMonth(days[i].date)}
        </text>
      ))}
    </svg>
  );
}
